import { type ThemeColors } from "../lib/theme";
import type { ReactNode, MouseEvent } from "react";
import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { SvgIcon } from "./ui/SvgIcon";
import boltIcon from "../assets/bolt-icon.svg?raw";

interface TargetBadgeProps {
  article: {
    id: number;
    title: string;
    description: string | null;
  };
  isFound: boolean;
  theme: ThemeColors;
  children?: ReactNode;
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  className?: string;
  isDark?: boolean;
}

export function TargetBadge({
  article,
  isFound,
  theme,
  children,
  onClick,
  className = "",
}: TargetBadgeProps) {
  const { t } = useTranslation();
  const [showDialog, setShowDialog] = useState(false);
  const [isMobile, setIsMobile] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(max-width: 1023px)").matches,
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 1023px)");
    const checkMobile = () => setIsMobile(mediaQuery.matches);

    checkMobile();
    mediaQuery.addEventListener("change", checkMobile);
    return () => mediaQuery.removeEventListener("change", checkMobile);
  }, []);

  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    if (isMobile && article.description) {
      setShowDialog(true);
    }
    onClick?.(e);
  };

  const truncatedDescription =
    article.description && article.description.length > 1000
      ? article.description.slice(0, 1000) + "..."
      : article.description;

  const content = (
    <>
      <span className="flex items-center gap-2.5 min-w-0">
        <span
          aria-hidden="true"
          className={`flex-shrink-0 flex items-center justify-center w-5 h-5 rounded-full border-2 border-ink text-[10px] leading-none font-display ${
            isFound ? "bg-mint text-ink" : "bg-ink text-gold"
          }`}
        >
          {isFound ? "✓" : "★"}
        </span>
        <span className={`truncate font-serif font-bold ${isFound ? "line-through decoration-2" : ""}`}>
          {article.title}
        </span>
      </span>
      {children && (
        <span className="flex-shrink-0 text-xs tabular-nums opacity-70">
          {children}
        </span>
      )}
    </>
  );

  const tooltipContent = truncatedDescription && (
    <span
      className={`absolute left-1/2 -translate-x-1/2 top-full mt-3 hidden lg:group-hover:block z-50 w-80 p-4 text-sm ${theme.panel} ${theme.text}`}
    >
      {truncatedDescription}
    </span>
  );

  const baseClasses = `group relative flex items-center justify-between gap-3 px-3.5 py-2.5 text-sm rounded-xl border-[3px] border-ink shadow-board-sm transition-transform duration-200 w-full ${className}`;

  const stateClasses = isFound
    ? `${theme.textSecondary} ${theme.bgSecondary} opacity-80`
    : "bg-gold text-ink";

  const dialogContent = showDialog && isMobile && (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200 cursor-pointer"
      role="dialog"
      aria-modal="true"
      aria-labelledby={`target-title-${article.id}`}
      onClick={() => setShowDialog(false)}
    >
      <div
        className={`w-full max-w-sm cursor-default animate-in zoom-in-95 duration-200 ${theme.panel}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div
              className="flex-shrink-0 w-12 h-12 -rotate-3 rounded-xl border-[3px] border-ink bg-gold text-ink shadow-board-sm flex items-center justify-center"
            >
              <SvgIcon markup={boltIcon} size={24} strokeWidth={1.5} />
            </div>
            <div className="flex-1 min-w-0">
              <h3
                id={`target-title-${article.id}`}
                className={`font-serif text-xl font-bold mb-1 ${theme.text}`}
              >
                {article.title}
              </h3>
              {article.description && (
                <p
                  className={`text-sm leading-relaxed ${theme.textSecondary} overflow-scroll h-96`}
                >
                  {truncatedDescription}
                </p>
              )}
            </div>
          </div>
        </div>
        <div
          className={`px-6 py-4 border-t-2 flex justify-end ${theme.divider}`}
        >
          <button
            onClick={() => setShowDialog(false)}
            className={theme.buttonSmall}
          >
            {t("components.targetBadge.close")}
          </button>
        </div>
      </div>
    </div>
  );
  const dialog =
    dialogContent && typeof document !== "undefined"
      ? createPortal(dialogContent, document.body)
      : null;

  if (onClick || isMobile) {
    return (
      <>
        <button
          type="button"
          onClick={handleClick}
          className={`${baseClasses} ${stateClasses} cursor-pointer hover:-rotate-1 active:translate-y-1 active:shadow-none`}
        >
          {content}
          {tooltipContent}
        </button>
        {dialog}
      </>
    );
  }

  return (
    <span className={`${baseClasses} ${stateClasses}`}>
      {content}
      {tooltipContent}
    </span>
  );
}
